import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

import { commerceEvent } from './commerceAnalytics';

const CartContext = createContext(null);
const STORAGE_KEY = 'vt_cart_v1';

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {}
  }, [items]);

  const addItem = useCallback((item) => {
    if (!items.some(p => p.product_id === item.product_id && p.size === item.size)) commerceEvent('add_to_cart', [{ ...item, quantity: 1 }]);
    setItems((prev) => {
      const key = `${item.product_id}-${item.size || ''}`;
      const existing = prev.find((p) => `${p.product_id}-${p.size || ''}` === key);
      if (existing) {
        // Each jersey is unique — already in cart, don't add a duplicate
        return prev;
      }
      return [...prev, { ...item, quantity: 1 }];
    });
    setIsOpen(true);
  }, [items]);

  const removeItem = useCallback((product_id, size) => {
    setItems((prev) => prev.filter((p) => !(p.product_id === product_id && p.size === size)));
  }, []);

  const updateQuantity = useCallback((product_id, size, quantity) => {
    setItems((prev) =>
      prev.map((p) =>
        p.product_id === product_id && p.size === size ? { ...p, quantity: Math.max(1, quantity) } : p
      )
    );
  }, []);

  const clear = useCallback(() => setItems([]), []);
  const restoreItems = useCallback((restored) => {
    if (Array.isArray(restored) && restored.length) setItems(restored);
  }, []);

  const count = items.reduce((sum, i) => sum + i.quantity, 0);
  const total = items.reduce((sum, i) => sum + i.price * i.quantity, 0);

  return (
    <CartContext.Provider
      value={{ items, isOpen, setIsOpen, addItem, removeItem, updateQuantity, clear, restoreItems, count, total }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}